import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { Button, message, Tooltip } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { observer } from 'mobx-react';

import { AutoComplete, Input } from '@/components/Common/UIKit';
import { useStores } from '@/components/hooks';
import i18n from '@/content';

import TagsList from './TagsList';
import PolicySetting, { PolicyRowProps } from '../PolicySetting';
import { ListTypeInputs } from './interfaces';
import OPTIONS from './options';
import './index.scss';

interface PolicyGroupAutocompleteRowProps extends PolicyRowProps<string[]> {
    listType: ListTypeInputs;
    hasError?: boolean;
    errorMessage?: string;
    controlReadOnly?: boolean;
    readOnlyTooltip?: string;
}

const MIN_SEARCH_LENGTH = 2;

const PolicyGroupAutocompleteRow: FC<PolicyGroupAutocompleteRowProps> = observer(({
    settingId,
    settingValue,
    handleChange,
    isChild,
    listType,
    hasError = false,
    errorMessage,
    controlReadOnly = false,
    readOnlyTooltip,
}) => {
    const { usersListStore } = useStores();
    const [tags, setTags] = useState<string[]>(settingValue);
    const [inputValue, setInputValue] = useState('');
    const [hasBeenFocused, setHasBeenFocused] = useState(false);

    const nameSpace = `policyEditor.tagsInput.${listType}`;

    useEffect(() => {
        setTags(settingValue);
    }, [settingValue]);

    useEffect(() => {
        usersListStore.fetchUsersCustomGroups();
    }, [usersListStore]);

    const groupsMap = useMemo(() => {
        const map = new Map<string, string>();
        usersListStore.userGroupsList.forEach(({ groupId, groupName }) => {
            if (groupId) map.set(groupId, groupName);
        });
        return map;
    }, [usersListStore.userGroupsList]);

    const autocompleteOptions = useMemo(() => {
        const trimmed = inputValue.trim().toLowerCase();
        if (trimmed.length < MIN_SEARCH_LENGTH || trimmed.includes('@')) return [];

        return usersListStore.userGroupsList
            .filter(({ groupName }) => groupName.toLowerCase().includes(trimmed))
            .map(({ groupId, groupName }) => ({ value: groupId, label: groupName }));
    }, [inputValue, usersListStore.userGroupsList]);

    const addTag = useCallback((value: string) => {
        if (controlReadOnly) {
            return;
        }
        const normalized = value.toLowerCase();
        if (tags.includes(normalized)) {
            message.warning(i18n.t(`${nameSpace}.alreadyIncluded`));
            return;
        }
        const newTags = [normalized, ...tags];
        setTags(newTags);
        setInputValue('');
        setHasBeenFocused(false);
        handleChange(settingId, newTags);
    }, [tags, nameSpace, settingId, handleChange, controlReadOnly]);

    const addManualEntry = useCallback(() => {
        if (controlReadOnly) {
            return;
        }
        const value = inputValue.trim().toLowerCase();
        if (!value) return;

        const isValid = value.match(OPTIONS[listType].validationRegexp);
        if (!isValid) {
            message.error(i18n.t(`${nameSpace}.notValid`));
            return;
        }
        addTag(value);
    }, [inputValue, listType, nameSpace, addTag, controlReadOnly]);

    const onSelect = useCallback((groupId: string) => {
        if (controlReadOnly) {
            return;
        }
        addTag(groupId);
    }, [addTag, controlReadOnly]);

    const onKeyDown = useCallback((event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            addManualEntry();
        }
    }, [addManualEntry]);

    const onDelete = useCallback((tag: string) => {
        if (controlReadOnly) {
            return;
        }
        const newTags = tags.filter((t) => t !== tag);
        setTags(newTags);
        handleChange(settingId, newTags);
    }, [tags, settingId, handleChange, controlReadOnly]);

    const shouldShowError = hasError && hasBeenFocused && !controlReadOnly;
    const inputClasses = classNames('add-tags', { 'has-error': shouldShowError });
    const hasTags = tags && !!tags.length;

    const autocompleteBlock = controlReadOnly ? (
        <AutoComplete
            disabled
            options={[]}
            onSelect={onSelect}
            onChange={setInputValue}
            value={inputValue}
        >
            <Input
                disabled
                onFocus={() => setHasBeenFocused(true)}
                onKeyDown={onKeyDown}
                className={inputClasses}
                placeholder={i18n.t(`${nameSpace}.placeholder`)}
                addonAfter={(
                    <Button type="link" disabled onClick={addManualEntry}>
                        <PlusOutlined />
                    </Button>
                )}
            />
        </AutoComplete>
    ) : (
        <Tooltip
            color="white"
            open={shouldShowError}
            title={errorMessage && (
                <span className="error-hint">{errorMessage}</span>
            )}
        >
            <AutoComplete
                options={autocompleteOptions}
                onSelect={onSelect}
                onChange={setInputValue}
                value={inputValue}
            >
                <Input
                    onFocus={() => setHasBeenFocused(true)}
                    onKeyDown={onKeyDown}
                    className={inputClasses}
                    placeholder={i18n.t(`${nameSpace}.placeholder`)}
                    addonAfter={(
                        <Button type="link" onClick={addManualEntry}>
                            <PlusOutlined />
                        </Button>
                    )}
                />
            </AutoComplete>
        </Tooltip>
    );

    const inner = (
        <div className="tags-input-wrapper">
            <PolicySetting
                isChild={isChild}
                settingId={settingId}
                type="tag"
                hideDescription
            >
                {autocompleteBlock}
            </PolicySetting>
            {hasTags && (
                <TagsList
                    readOnly={controlReadOnly}
                    onDelete={onDelete}
                    tagsList={tags}
                    groupsMap={groupsMap}
                />
            )}
        </div>
    );

    if (controlReadOnly && readOnlyTooltip) {
        return <Tooltip title={readOnlyTooltip}>{inner}</Tooltip>;
    }

    return inner;
});

export default PolicyGroupAutocompleteRow;
